import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { fetchBlueprint } from 'state';
import { readableError } from '../ScenarioBuilder/errors';
import {
  AnswerKeyCoverageDocument,
  fetchAnswerKeyCoverage,
  fetchAssessmentGeneration,
  retryAssessmentGeneration,
} from './api';

export const ASSESSMENT_POLL_MS = 3000;

const pending = (document: AnswerKeyCoverageDocument | null) =>
  ['queued', 'running'].includes(document?.regeneration?.status || '');

type Snapshot = {
  context: string;
  document: AnswerKeyCoverageDocument | null;
  error: string;
  loading: boolean;
  retrying: boolean;
};

/** Watch committed edits and the durable worker status, independently of sockets. */
export default function useAnswerKeyCoverage({
  blueprintId,
  variantId,
  refreshToken,
  statusOnly = false,
  enabled = true,
}: {
  blueprintId: number;
  variantId?: number;
  refreshToken?: unknown;
  statusOnly?: boolean;
  enabled?: boolean;
}) {
  const dispatch = useDispatch();
  const context = `${blueprintId}:${variantId || ''}`;
  const [reloadToken, setReloadToken] = useState(0);
  const [snapshot, setSnapshot] = useState<Snapshot>({
    context,
    document: null,
    error: '',
    loading: Boolean(variantId),
    retrying: false,
  });
  const observed = useRef<{
    context: string;
    document: AnswerKeyCoverageDocument | null;
  }>({ context, document: null });
  const activeContext = useRef(context);
  activeContext.current = context;
  const retryRequest = useRef(0);
  useEffect(
    () => () => {
      // A retry belongs to this visit, even if the admin leaves and later
      // returns to the same variant before its HTTP response arrives.
      retryRequest.current += 1;
    },
    [context],
  );

  const refresh = useCallback(() => setReloadToken(value => value + 1), []);

  useEffect(() => {
    if (!enabled || !blueprintId || !variantId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async (statusRead = statusOnly) => {
      setSnapshot(previous => ({
        context,
        document: previous.context === context ? previous.document : null,
        error: '',
        loading: true,
        retrying: previous.context === context && previous.retrying,
      }));
      let pollAgain =
        observed.current.context === context &&
        pending(observed.current.document);
      try {
        let document: AnswerKeyCoverageDocument;
        if (statusRead) {
          const result = await fetchAssessmentGeneration(
            blueprintId,
            variantId,
          );
          if (cancelled) return;
          document = {
            ...(observed.current.context === context
              ? observed.current.document || { key: null }
              : { key: null }),
            regeneration: result.regeneration,
          };
          // Polling reads only durable status. Refresh the full key and its
          // audit once, after the update settles, before displaying it as ready.
          if (pollAgain && !pending(document) && !statusOnly) {
            document = await fetchAnswerKeyCoverage(blueprintId, variantId);
          }
        } else {
          document = await fetchAnswerKeyCoverage(blueprintId, variantId);
        }
        if (cancelled) return;
        const finished = pollAgain && !pending(document);
        observed.current = { context, document };
        pollAgain = pending(document);
        setSnapshot(previous => ({
          ...previous,
          context,
          document,
          error: '',
          loading: false,
        }));
        if (finished) {
          dispatch(
            fetchBlueprint({
              id: blueprintId,
              summary_scope: 'latest',
              summary_variant_id: variantId,
            }),
          );
        }
      } catch (error) {
        if (cancelled) return;
        setSnapshot(previous => ({
          ...previous,
          error: readableError(error, { what: "This variant's answer key" }),
          loading: false,
        }));
      }
      // A transient read failure must not strand an in-flight regeneration.
      if (!cancelled && pollAgain) {
        timer = setTimeout(() => void load(true), ASSESSMENT_POLL_MS);
      }
    };

    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    blueprintId,
    variantId,
    context,
    refreshToken,
    reloadToken,
    dispatch,
    statusOnly,
    enabled,
  ]);

  const retry = useCallback(async () => {
    if (!blueprintId || !variantId) return;
    const request = ++retryRequest.current;
    setSnapshot(previous => ({ ...previous, retrying: true, error: '' }));
    try {
      const result = await retryAssessmentGeneration(blueprintId, variantId);
      if (
        activeContext.current !== context ||
        retryRequest.current !== request
      ) {
        return;
      }
      if (observed.current.context === context && observed.current.document) {
        observed.current = {
          context,
          document: {
            ...observed.current.document,
            regeneration: result.regeneration,
          },
        };
      }
      setSnapshot(previous => ({
        ...previous,
        retrying: false,
        document: previous.document
          ? { ...previous.document, regeneration: result.regeneration }
          : null,
      }));
      refresh();
    } catch (error) {
      if (
        activeContext.current !== context ||
        retryRequest.current !== request
      ) {
        return;
      }
      setSnapshot(previous => ({
        ...previous,
        retrying: false,
        error: readableError(error, { what: 'Assessment regeneration' }),
      }));
    }
  }, [blueprintId, variantId, context, refresh]);

  const current = snapshot.context === context;
  return {
    document: enabled && current ? snapshot.document : null,
    loading: enabled && (current ? snapshot.loading : Boolean(variantId)),
    error: current ? snapshot.error : '',
    retrying: current && snapshot.retrying,
    refresh,
    retry,
  };
}
